import { useState, useEffect } from 'react'
import { X, Save } from 'lucide-react'
import { useToast } from '../../contexts/ToastContext'
import { supabase } from '../../lib/supabase'

interface Module {
  id?: string
  course_id: string
  title: string
  description: string | null
  order_index: number
  duration_minutes: number | null
  is_published: boolean
}

interface ModuleModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (module: Module) => void
  courseId: string
  module?: Module | null
  maxOrderIndex: number
}

export default function ModuleModal({
  isOpen,
  onClose,
  onSave,
  courseId,
  module,
  maxOrderIndex
}: ModuleModalProps) {
  const [formData, setFormData] = useState<Module>({
    course_id: courseId,
    title: '',
    description: '',
    order_index: maxOrderIndex + 1,
    duration_minutes: null,
    is_published: false
  })
  const [saving, setSaving] = useState(false)
  const { showToast } = useToast()

  useEffect(() => {
    if (module) {
      setFormData(module)
    } else {
      setFormData({
        course_id: courseId,
        title: '',
        description: '',
        order_index: maxOrderIndex + 1,
        duration_minutes: null,
        is_published: false
      })
    }
  }, [module, courseId, maxOrderIndex])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.title.trim()) {
      showToast('error', 'El título es obligatorio')
      return
    }

    setSaving(true)

    try {
      if (module?.id) {
        // Actualizar módulo existente
        const { data, error } = await supabase
          .from('course_modules')
          .update({
            title: formData.title,
            description: formData.description || null,
            duration_minutes: formData.duration_minutes || null,
            is_published: formData.is_published
          })
          .eq('id', module.id)
          .select()
          .single()

        if (error) throw error
        showToast('success', 'Módulo actualizado exitosamente')
        onSave(data)
      } else {
        // Crear nuevo módulo
        const { data, error } = await supabase
          .from('course_modules')
          .insert([{
            course_id: courseId,
            title: formData.title,
            description: formData.description || null,
            order_index: formData.order_index,
            duration_minutes: formData.duration_minutes || null,
            is_published: formData.is_published
          }])
          .select()
          .single()

        if (error) throw error
        showToast('success', 'Módulo creado exitosamente')
        onSave(data)
      }

      onClose()
    } catch (error: any) {
      console.error('Error saving module:', error)
      const errorMessage = 'Error al guardar módulo: ' + error.message
      showToast('error', errorMessage)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-education px-6 py-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">
              {module ? 'Editar Módulo' : 'Crear Nuevo Módulo'}
            </h3>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSave} className="p-6 space-y-4">
            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Título <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                placeholder="Ej: Introducción a la Sostenibilidad"
              />
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción
              </label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                placeholder="Breve descripción del módulo"
              />
            </div>

            {/* Duración */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duración (minutos)
              </label>
              <input
                type="number"
                min="0"
                value={formData.duration_minutes || ''}
                onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value ? parseInt(e.target.value) : null })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
                placeholder="Ej: 45"
              />
            </div>

            {/* Estado de Publicación */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_published"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="h-4 w-4 text-education focus:ring-education border-gray-300 rounded"
              />
              <label htmlFor="is_published" className="ml-2 block text-sm text-gray-700">
                Publicar módulo (visible para estudiantes)
              </label>
            </div>

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-education text-white rounded-lg hover:bg-education/90 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Guardando...' : 'Guardar Módulo'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
